import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Editor } from "@tiptap/core";
import { TextSelection, type SelectionBookmark } from "@tiptap/pm/state";
import { Heading1, Heading2, Heading3, Heading4, List, ListOrdered, ListChecks, ImagePlus, Paperclip, SquareCode, Quote, LoaderCircle, Table2, Link2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { applySlashCommand, dismissSlash, matchingCommands, slashKey, type SlashCommandId, type SlashMatch } from "./slashCommands";
import { uploadAttachment, mediaFileTypes } from "./attachments";
import { errorMessage } from "@/lib/api";
import { EntityLinkPicker, entityLinkHref } from "./EntityLinkPicker";
import { insertEntityLink } from "./entityLinkInsertion";

const icons = { heading1: Heading1, heading2: Heading2, heading3: Heading3, heading4: Heading4, bulletList: List, orderedList: ListOrdered, taskList: ListChecks, link: Link2, media: ImagePlus, file: Paperclip, table: Table2, codeBlock: SquareCode, blockquote: Quote };
const hints: Partial<Record<SlashCommandId, string>> = { heading1: "#", heading2: "##", heading3: "###", heading4: "####", bulletList: "-", orderedList: "1.", taskList: "[ ]", codeBlock: "```", blockquote: ">" };

/** Shared menu: editor keeps focus so selection, IME, dialogs and blur saves work. */
export function SlashMenu({ editor, projectId, onBusyChange }: { editor: Editor | null; projectId?: string; onBusyChange?: (busy: boolean) => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const [menu, setMenu] = useState<{ match: SlashMatch; left: number; top: number; maxHeight: number } | null>(null);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const [linkAnchor, setLinkAnchor] = useState<{ left: number; top: number } | null>(null);
  const link = useRef<{ bookmark: SelectionBookmark; query: string } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const upload = useRef<{ kind: "media" | "file"; bookmark: SelectionBookmark; query: string } | null>(null);
  const controller = useRef<AbortController | null>(null);
  const busyCallback = useRef(onBusyChange);
  useEffect(() => { busyCallback.current = onBusyChange; }, [onBusyChange]);

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    const update = () => {
      const match = slashKey.getState(editor.state)?.active;
      if (!match || link.current || !editor.isEditable || !editor.isFocused) { setMenu(null); return; }
      const rect = editor.view.coordsAtPos(match.from);
      const below = window.innerHeight - rect.bottom - 16;
      const above = rect.top - 16;
      const height = Math.min(480, Math.max(160, below >= 260 || below >= above ? below : above));
      setMenu({ match, left: Math.max(12, Math.min(rect.left, window.innerWidth - 316)), top: below >= 260 || below >= above ? rect.bottom + 8 : rect.top - height - 8, maxHeight: height });
    };
    const mapSelection = ({ transaction }: { transaction: import("@tiptap/pm/state").Transaction }) => {
      if (upload.current) upload.current.bookmark = upload.current.bookmark.map(transaction.mapping);
      if (link.current) link.current.bookmark = link.current.bookmark.map(transaction.mapping);
      update();
    };
    const linkCommand = (event: Event) => {
      if (!projectId || link.current || controller.current) return;
      const { range } = (event as CustomEvent<{ range: SlashMatch }>).detail;
      const rect = editor.view.coordsAtPos(range.from);
      link.current = { bookmark: TextSelection.create(editor.state.doc, range.from, range.to).getBookmark(), query: editor.state.doc.textBetween(range.from, range.to) };
      busyCallback.current?.(true);
      dismissSlash(editor);
      setLinkAnchor({ left: rect.left, top: rect.bottom });
    };
    const fileCommand = (event: Event) => {
      const { id, range } = (event as CustomEvent<{ id: "media" | "file"; range: SlashMatch }>).detail;
      if (controller.current || !projectId) return;
      upload.current = { kind: id, bookmark: TextSelection.create(editor.state.doc, range.from, range.to).getBookmark(), query: editor.state.doc.textBetween(range.from, range.to) };
      busyCallback.current?.(true);
      setError("");
      dismissSlash(editor);
      input.current!.accept = id === "media" ? mediaFileTypes.join(",") : "";
      input.current!.value = "";
      input.current!.click();
    };
    const cancel = () => { upload.current = null; busyCallback.current?.(false); if (!editor.isDestroyed) editor.commands.focus(); };
    const scroll = (event: Event) => {
      if (event.target instanceof Element && event.target.closest("[data-slash-menu]")) return;
      update();
    };
    // Radix dialogs listen on document capture, before ProseMirror receives the
    // key. Handle an open menu at window capture so Escape preserves the draft.
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing || event.keyCode === 229 || editor.view.composing || !editor.isFocused || !editor.isEditable || !slashKey.getState(editor.state)?.active) return;
      event.preventDefault(); event.stopPropagation();
      dismissSlash(editor);
    };
    const picker = input.current;
    picker?.addEventListener("cancel", cancel);
    dom.addEventListener("tern:file-command", fileCommand);
    dom.addEventListener("tern:link-command", linkCommand);
    editor.on("transaction", mapSelection).on("focus", update).on("blur", update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("keydown", escape, true);
    return () => {
      controller.current?.abort();
      controller.current = null;
      upload.current = null;
      link.current = null;
      busyCallback.current?.(false);
      picker?.removeEventListener("cancel", cancel);
      dom.removeEventListener("tern:file-command", fileCommand);
      dom.removeEventListener("tern:link-command", linkCommand);
      editor.off("transaction", mapSelection).off("focus", update).off("blur", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("keydown", escape, true);
      dom.removeAttribute("aria-controls"); dom.removeAttribute("aria-activedescendant"); dom.removeAttribute("aria-expanded");
    };
  }, [editor, projectId]);

  const commands = menu ? matchingCommands(menu.match.query) : [];
  const activeIndex = menu?.match.index;
  const activeQuery = menu?.match.query;
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    if (menu) {
      dom.setAttribute("aria-controls", listId);
      dom.setAttribute("aria-expanded", "true");
      if (commands.length) dom.setAttribute("aria-activedescendant", `${listId}-${menu.match.index}`);
      else dom.removeAttribute("aria-activedescendant");
    } else {
      dom.removeAttribute("aria-controls"); dom.removeAttribute("aria-activedescendant"); dom.removeAttribute("aria-expanded");
    }
  }, [editor, menu, listId, commands.length]);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, activeQuery]);

  const choose = (id: SlashCommandId) => {
    if (!editor || !menu) return;
    if (id === "link") editor.view.dom.dispatchEvent(new CustomEvent("tern:link-command", { detail: { range: menu.match } }));
    else if (id === "media" || id === "file") editor.view.dom.dispatchEvent(new CustomEvent("tern:file-command", { detail: { id, range: menu.match } }));
    else applySlashCommand(editor, id, menu.match);
  };

  const closeLink = () => {
    link.current = null;
    setLinkAnchor(null);
    busyCallback.current?.(false);
  };

  const filesSelected = async (files: FileList | null) => {
    if (!editor || !projectId || !upload.current || !files?.length) {
      busyCallback.current?.(false);
      return;
    }
    const abort = new AbortController();
    controller.current = abort;
    try {
      for (const file of Array.from(files)) {
        if (upload.current.kind === "media" && !mediaFileTypes.includes(file.type)) throw new Error(t("editor.mediaTypeError"));
        if (!file.size || file.size > 25 * 1024 * 1024) throw new Error(t("editor.fileSizeError"));
        setProgress(t("editor.uploading", { name: file.name }));
        const saved = await uploadAttachment(projectId, file, abort.signal);
        if (editor.isDestroyed || abort.signal.aborted) return;
        const target = upload.current.bookmark.resolve(editor.state.doc);
        const remove = editor.state.doc.textBetween(target.from, target.to) === upload.current.query;
        const at = remove ? { from: target.from, to: target.to } : { from: target.to, to: target.to };
        const isMedia = upload.current.kind === "media";
        const node = isMedia
          ? { type: "image", attrs: { src: saved.url, alt: saved.name, title: saved.content_type.startsWith("video/") ? "tern-video" : null } }
          : { type: "paragraph", content: [{ type: "text", text: saved.name, marks: [{ type: "link", attrs: { href: `${saved.url}?download=true` } }] }] };
        editor.chain().focus().insertContentAt(at, [node, { type: "paragraph" }]).run();
        upload.current.bookmark = editor.state.selection.getBookmark();
        upload.current.query = "";
      }
    } catch (err) {
      if (!abort.signal.aborted) setError(err instanceof Error && !("status" in err) ? err.message : errorMessage(err));
    } finally {
      if (!abort.signal.aborted) {
        setProgress(""); upload.current = null; controller.current = null;
        busyCallback.current?.(false);
        if (!editor.isDestroyed) editor.commands.focus();
      }
    }
  };

  return <>
    {linkAnchor && editor && projectId && <EntityLinkPicker projectId={projectId} anchor={linkAnchor} onClose={closeLink} onRestoreFocus={() => { if (!editor.isDestroyed) editor.commands.focus(); }} onPick={(target) => {
      if (link.current && !editor.isDestroyed && editor.isEditable) {
        insertEntityLink(editor, link.current.bookmark, link.current.query, target.title, entityLinkHref(target));
      }
      closeLink();
    }} />}
    <input ref={input} type="file" multiple className="hidden" tabIndex={-1} aria-label={t("editor.file")} onChange={(event) => { void filesSelected(event.target.files); }} />
    {progress && <div className="flex items-center gap-2 px-1 py-2 text-[12px] text-muted" role="status"><LoaderCircle className="h-3.5 w-3.5 animate-spin" />{progress}</div>}
    {error && <div className="px-1 py-2 text-[12px] text-danger" role="alert">{error}</div>}
    {menu && createPortal(
      <div data-slash-menu className="fixed z-[100] flex w-[300px] flex-col overflow-hidden rounded-xl border border-border-strong bg-elevated text-fg shadow-[0_12px_40px_#0005]" style={{ left: menu.left, top: menu.top, maxHeight: menu.maxHeight, pointerEvents: "auto" }} onPointerDown={(event) => { event.preventDefault(); }} onMouseDown={(event) => event.preventDefault()}>
        <div ref={list} id={listId} role="listbox" aria-label={t("editor.commands")} className="min-h-0 overflow-y-auto overscroll-contain p-1.5">
          {commands.map((command, index) => {
            const Icon = icons[command.id];
            const separator = index > 0 && commands[index - 1]!.group !== command.group;
            return <div key={command.id} className={separator ? "mt-1.5 border-t border-border pt-1.5" : ""}>
              <button type="button" tabIndex={-1} id={`${listId}-${index}`} role="option" aria-selected={menu.match.index === index} className="flex h-9 w-full items-center gap-3 rounded-md px-3 text-left text-[13px] font-medium text-fg aria-selected:bg-active hover:bg-hover disabled:opacity-40" disabled={["link", "media", "file"].includes(command.id) && !projectId} onMouseMove={() => { if (editor && index !== menu.match.index) editor.view.dispatch(editor.state.tr.setMeta(slashKey, { index })); }} onClick={() => choose(command.id)}>
                <Icon className="h-[18px] w-[18px] shrink-0 text-muted" /><span className="flex-1">{t(`editor.${command.id}`)}</span><span className="font-mono text-[11px] font-normal text-faint">{hints[command.id]}</span>
              </button>
            </div>;
          })}
          {!commands.length && <div className="px-3 py-5 text-[13px] text-muted">{t("editor.noCommands")}</div>}
        </div>
        <div className="flex shrink-0 justify-between border-t border-border px-3 py-2 text-[10px] text-faint"><span>↑ ↓ {t("editor.navigate")} <span className="ml-2">↵ {t("editor.select")}</span></span><span>esc {t("editor.dismiss")}</span></div>
      </div>, document.body,
    )}
  </>;
}
